import { settingsQueryKeys } from '@features/settings/api/queryKeys'
import { isCancelledError, type QueryClient } from '@tanstack/react-query'
import { getConfiguration } from '../api/configuration.api'
import { normalizeRecordAttributes } from '../../records/api/recordAttributes.api'
import { useSettingsStore, type EffectiveSettings } from '../stores/useSettingsStore'
import {
  preferenceEditSlotHasChanges,
  usePreferenceEditingStore,
} from '../stores/usePreferenceEditingStore'
import {
  effectiveSettingsPatchFromPreferenceDefaults,
  effectiveSettingsPatchFromUserPreferences,
} from '../utils/preferenceEffectiveSettings'
import type { ResolvedConfiguration } from '../models'

const CHANNEL_NAME = 'arvoke:settings-resolution'
const STORAGE_KEY = 'arvoke.settings-resolution.updated'

// This tab's ID. A BroadcastChannel also delivers to other channels in the
// sending tab, which already updated itself; it ignores its own messages.
const TAB_ID =
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random()}`

interface SettingsResolutionMessage {
  type: 'settings-resolution-changed'
  changedAt: number
  sourceTabId?: string
}

function isSettingsResolutionMessage(value: unknown): value is SettingsResolutionMessage {
  return (
    typeof value === 'object' &&
    value !== null &&
    (value as { type?: unknown }).type === 'settings-resolution-changed' &&
    typeof (value as { changedAt?: unknown }).changedAt === 'number'
  )
}

export function broadcastSettingsResolutionChanged() {
  const message: SettingsResolutionMessage = {
    type: 'settings-resolution-changed',
    changedAt: Date.now(),
    sourceTabId: TAB_ID,
  }

  if (typeof window === 'undefined') {
    return
  }

  if (typeof window.BroadcastChannel === 'function') {
    const channel = new window.BroadcastChannel(CHANNEL_NAME)

    channel.postMessage(message)
    channel.close()

    return
  }

  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(message))
}

export function hydrateResolvedConfiguration(configuration: ResolvedConfiguration) {
  const settingsStore = useSettingsStore.getState()

  settingsStore.hydrateConfiguration(configuration)

  settingsStore.setRecordAttributes(
    normalizeRecordAttributes({
      app_data_types: configuration.app_data_types,
    })
  )

  const previewPatch = activePreferencePreviewPatch()

  if (Object.keys(previewPatch).length > 0) {
    settingsStore.applyEffectiveSettings(previewPatch)
  }
}

function activePreferencePreviewPatch(): Partial<EffectiveSettings> {
  const editingStore = usePreferenceEditingStore.getState()

  const tenantPreferencesSlot = editingStore.preferenceDefaults

  const userPreferencesSlot = editingStore.userPreferences

  const userPreferences = userPreferencesSlot.draft ?? userPreferencesSlot.saved

  const patch: Partial<EffectiveSettings> = {}

  if (preferenceEditSlotHasChanges(tenantPreferencesSlot)) {
    Object.assign(
      patch,
      effectiveSettingsPatchFromPreferenceDefaults(tenantPreferencesSlot.draft, userPreferences)
    )
  }

  if (preferenceEditSlotHasChanges(userPreferencesSlot)) {
    Object.assign(patch, effectiveSettingsPatchFromUserPreferences(userPreferencesSlot.draft))
  }

  return patch
}

export async function refetchSettingsResolution(queryClient: QueryClient) {
  // Every settings query (preference defaults, groups, your preferences) is
  // under this key. Invalidating one of them again would cancel its refetch
  // and send another request for it.
  await queryClient.invalidateQueries({ queryKey: settingsQueryKeys.settings })

  const configuration = await queryClient.fetchQuery({
    queryKey: settingsQueryKeys.configuration,
    queryFn: ({ signal }) => getConfiguration(signal),
  })

  hydrateResolvedConfiguration(configuration)
}

/**
 * After users move to another preference group: preference screens refetch,
 * and if the signed-in user moved, this tab and their other tabs apply what
 * their preferences now resolve to (theme, Home Page, and so on).
 */
export function refreshPreferenceGroupMoves(queryClient: QueryClient, userIds: Iterable<string>) {
  const appUserId = useSettingsStore.getState().appUserId
  if (!appUserId || !new Set(userIds).has(appUserId)) {
    void queryClient.invalidateQueries({ queryKey: settingsQueryKeys.preferenceGroups })
    return
  }

  // Refetching the resolution refetches the preference screens too.
  broadcastSettingsResolutionChanged()
  refetchSettingsResolution(queryClient).catch((error) => {
    // A newer change cancelled this refetch and started its own.
    if (isCancelledError(error)) return
    if (import.meta.env.DEV) console.error('Failed to reconcile resolved configuration', error)
  })
}

export function subscribeToSettingsResolutionChanges(
  onMessage: (message: SettingsResolutionMessage) => void
) {
  if (typeof window === 'undefined') return () => {}

  if (typeof window.BroadcastChannel === 'function') {
    const channel = new window.BroadcastChannel(CHANNEL_NAME)
    channel.onmessage = (event) => {
      if (isSettingsResolutionMessage(event.data) && event.data.sourceTabId !== TAB_ID) {
        onMessage(event.data)
      }
    }
    return () => channel.close()
  }

  const handleStorage = (event: StorageEvent) => {
    if (event.key !== STORAGE_KEY || !event.newValue) return
    try {
      const message = JSON.parse(event.newValue) as unknown
      if (isSettingsResolutionMessage(message)) onMessage(message)
    } catch {
      // Ignore malformed storage messages.
    }
  }

  window.addEventListener('storage', handleStorage)
  return () => window.removeEventListener('storage', handleStorage)
}
